// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * "There is a call happening, and it is not in this window."
 *
 * The call lives in a window of its own, which is what makes it survive
 * navigation and reachable from anywhere — and also what makes it easy to lose
 * behind a dozen other tabs. This is the way back.
 *
 * It names the patient rather than saying only "call in progress", because
 * focusing another window is best-effort: Chrome and Firefox honour it, Safari
 * often will not, and nothing reports the failure. When the button does
 * nothing, the patient's name is what lets someone find the window by eye.
 *
 * ## Why a bar across the middle rather than a floating card
 *
 * A floating card sits on top of whatever is underneath it, which in a
 * workspace this dense is always something. The bar spans the working area
 * only: it starts after the sidebar and stops at the conversation drawer when
 * that is open, so nothing it could obscure is ever behind it.
 */

import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { ActionIcon, Button, Group, Text, Tooltip } from '@mantine/core';
import {
  IconMicrophone,
  IconMicrophoneOff,
  IconPhone,
  IconPhoneOff,
  IconScreenShare,
  IconScreenShareOff,
  IconVideo,
  IconVideoOff,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useSidebar } from '../../contexts/SidebarContext';
import { useCareComm } from '../../contexts/CareCommContext';
import {
  CONVERSATION_PANEL_WIDTH,
  focusCallWindow,
  isCallWindow,
  sendCallCommand,
  subscribeToCallClaim,
} from '../../utils/callWindow';
import type { CallClaim } from '../../utils/callWindow';
import classes from './CallInOtherWindow.module.css';

/** Matches AppLayout's navbar width, which is the thing being cleared. */
function sidebarWidth(mode: string, isOpen: boolean, isExpanded: boolean): number {
  if (!isOpen) {
    return 0;
  }
  return mode === 'hover' && !isExpanded ? 68 : 240;
}

export function CallInOtherWindow(): JSX.Element | null {
  const { t } = useTranslation();
  const [claim, setClaim] = useState<CallClaim>();
  const { mode, isOpen, isExpanded } = useSidebar();
  const { isPanelOpen } = useCareComm();

  useEffect(() => {
    // The window holding the call shows the call, not a pointer to itself.
    if (isCallWindow()) {
      return undefined;
    }
    return subscribeToCallClaim(setClaim);
  }, []);

  if (!claim) {
    return null;
  }

  return (
    <div
      className={classes.callBar}
      style={{
        left: sidebarWidth(mode, isOpen, isExpanded),
        right: isPanelOpen ? CONVERSATION_PANEL_WIDTH : 0,
      }}
    >
      <Group h="100%" px="md" gap="sm" wrap="nowrap" justify="space-between">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          <IconPhone size={16} className={classes.icon} />
          <Text size="sm" truncate>
            {t('carecomm.callInOtherWindow', 'Call in progress with {{name}}', {
              name: claim.patientName,
            })}
          </Text>
        </Group>
        {/*
          * Mute and hang up without leaving what you are reading. The command
          * crosses to the call window rather than being handled here, because
          * the microphone is there — this bar is a remote control, not a
          * second copy of the call.
          *
          * The microphone is drawn as it actually is, from the call window's
          * own state. A button that always showed "unmuted" would invite a
          * second press, and the second press is the one that unmutes someone
          * who meant to stay quiet.
          */}
        <Group gap="xs" wrap="nowrap">
          <Tooltip
            label={claim.isMuted ? t('carecomm.unmute', 'Unmute') : t('carecomm.mute', 'Mute')}
          >
            <ActionIcon
              variant={claim.isMuted ? 'filled' : 'subtle'}
              color={claim.isMuted ? 'red' : 'gray'}
              size="sm"
              aria-label={claim.isMuted ? t('carecomm.unmute', 'Unmute') : t('carecomm.mute', 'Mute')}
              onClick={() => sendCallCommand('toggle-mute')}
            >
              {claim.isMuted ? <IconMicrophoneOff size={14} /> : <IconMicrophone size={14} />}
            </ActionIcon>
          </Tooltip>
          {/*
            * Camera and screen share only for a video call. On a voice call
            * they would be two controls that cannot do anything — and a
            * disabled camera button on a call that never had one reads as a
            * fault rather than as a kind of call.
            */}
          {claim.type === 'video-call' && (
            <>
              <Tooltip
                label={
                  claim.isCameraOff
                    ? t('carecomm.startVideo', 'Turn camera on')
                    : t('carecomm.stopVideo', 'Turn camera off')
                }
              >
                <ActionIcon
                  variant={claim.isCameraOff ? 'filled' : 'subtle'}
                  color={claim.isCameraOff ? 'red' : 'gray'}
                  size="sm"
                  aria-label={
                    claim.isCameraOff
                      ? t('carecomm.startVideo', 'Turn camera on')
                      : t('carecomm.stopVideo', 'Turn camera off')
                  }
                  onClick={() => sendCallCommand('toggle-camera')}
                >
                  {claim.isCameraOff ? <IconVideoOff size={14} /> : <IconVideo size={14} />}
                </ActionIcon>
              </Tooltip>
              <Tooltip
                label={
                  claim.isScreenSharing
                    ? t('carecomm.stopSharing', 'Stop sharing')
                    : t('carecomm.shareScreen', 'Share screen')
                }
              >
                <ActionIcon
                  variant={claim.isScreenSharing ? 'filled' : 'subtle'}
                  color={claim.isScreenSharing ? 'teal' : 'gray'}
                  size="sm"
                  aria-label={
                    claim.isScreenSharing
                      ? t('carecomm.stopSharing', 'Stop sharing')
                      : t('carecomm.shareScreen', 'Share screen')
                  }
                  onClick={() => sendCallCommand('toggle-screenshare')}
                >
                  {claim.isScreenSharing ? (
                    <IconScreenShareOff size={14} />
                  ) : (
                    <IconScreenShare size={14} />
                  )}
                </ActionIcon>
              </Tooltip>
            </>
          )}
          <Tooltip label={t('carecomm.endCall', 'End call')}>
            <ActionIcon
              variant="filled"
              color="red"
              size="sm"
              aria-label={t('carecomm.endCall', 'End call')}
              onClick={() => sendCallCommand('end-call')}
            >
              <IconPhoneOff size={14} />
            </ActionIcon>
          </Tooltip>
          <Button size="xs" variant="white" onClick={() => focusCallWindow()}>
            {t('carecomm.returnToCall', 'Return to call')}
          </Button>
        </Group>
      </Group>
    </div>
  );
}
